import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, BookText, Languages, Settings } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { ImageLoader } from "../../../ui/components/ImageLoader";
import { useAuth } from "../../lib/auth/hooks";
import { getAuthCookie } from "../../lib/auth/session-cookie";
import { useI18n } from "../../lib/i18n";
import * as localizationApi from "../../lib/api/localization";
import type { Language } from "../../lib/api/localization";
import { LanguageFormModal } from "./LanguageFormModal";
import * as s from "../products/ProductPage.css";
import { vars } from "../../../ui/styles/theme.css";

export function LanguageDetailPage({ languageId }: { languageId: string }) {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { t } = useI18n();
  const token = accessToken ?? getAuthCookie("access_token");

  const [language, setLanguage] = React.useState<Language | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    localizationApi
      .fetchLanguageById(token, languageId)
      .then((row) => {
        if (cancelled) return;
        if (!row) {
          setError(t("pro.system.localization.error.languageNotFound"));
          setLanguage(null);
        } else {
          setLanguage(row);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || t("pro.system.localization.error.loadLanguage"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, languageId, t]);

  const handleBack = () => navigate({ to: "/system/localization" });

  if (!token) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <Callout variant="error">
            {t("pro.system.localization.signInRequired")}
          </Callout>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <Text level="body-medium" style={{ color: vars.color.fgMuted }}>
            {t("shared.common.loading")}
          </Text>
        </div>
      </div>
    );
  }

  if (error || !language) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <button type="button" className={s.backLink} onClick={handleBack}>
            <ArrowLeft size={14} /> {t("pro.system.localization.title")}
          </button>
          <Callout variant="error">
            {error ?? t("pro.system.localization.error.languageNotFound")}
          </Callout>
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      <div className={s.pageColumn}>
        <button type="button" className={s.backLink} onClick={handleBack}>
          <ArrowLeft size={14} /> {t("pro.system.localization.title")}
        </button>

        <div className={s.header}>
          <div className={s.headerLeft}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {language.flagUrl ? (
                <div
                  style={{
                    width: 40,
                    height: 28,
                    borderRadius: 4,
                    overflow: "hidden",
                    flexShrink: 0,
                    border: `1px solid ${vars.color.border}`,
                  }}
                >
                  <ImageLoader src={language.flagUrl} />
                </div>
              ) : (
                <Languages size={22} color={vars.color.fgMuted} />
              )}
              <Text level="h2" weight="bold">
                {language.name}
              </Text>
              <span
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 13,
                  color: vars.color.fgMuted,
                }}
              >
                {language.code}
              </span>
              {!language.enabled && (
                <span
                  className={s.statusBadge}
                  style={{ background: vars.color.muted, color: vars.color.fgMuted }}
                >
                  {t("pro.system.localization.status.disabled")}
                </span>
              )}
            </div>
            {language.description && (
              <Text level="body-medium" style={{ color: vars.color.fgMuted }}>
                {language.description}
              </Text>
            )}
          </div>
          <div className={s.headerActions}>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigate({ to: "/system/localization/definitions" })
              }
            >
              <BookText size={14} style={{ marginRight: 4 }} />
              {t("pro.system.localization.definitions")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditOpen(true)}
            >
              <Settings size={14} style={{ marginRight: 4 }} />
              {t("pro.system.localization.editLanguage")}
            </Button>
          </div>
        </div>

        <LanguageFormModal
          existing={language}
          open={editOpen}
          setOpen={setEditOpen}
          onSaved={(saved) => setLanguage(saved)}
        />
      </div>
    </div>
  );
}
